import { useEffect, useState } from "react";
import { motion } from 'framer-motion';
import styled from "styled-components";
import { useQuiz } from "../contexts/QuizContext";
import { useNavigate } from "react-router-dom";
import { trackGatewaySelection, retryPendingWebhooks } from "../utils/tracking";
import { useUser } from "../contexts/UserContext";
import { PageContainer, Title, Subtitle, LinkButton } from '../styles/SharedComponents';

// Declaração para o Facebook Pixel
declare global {
  interface Window {
    fbq: any;
    fbPixelBypass?: {
      isLoaded: () => boolean;
      sendEvent: (eventName: string, eventData: any) => boolean;
      forceBeacon: (eventName: string, eventData: any) => boolean;
    }
  }
}

const ResultsHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    margin-bottom: 2.5rem;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 2rem;
  }
`;

const ResultsWrapper = styled.div`
  width: 100%;
  max-width: 900px;
`;

// Estendendo o Card como um componente de motion
const AnimatedCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    padding: 2rem;
  }
  
  @media (max-width: 480px) {
    padding: 1.25rem;
    margin-bottom: 1.25rem;
    border-radius: 10px;
  }
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3);
  }
`;

const GatewayInfo = styled.div`
  flex: 1;
`;

const GatewayName = styled.h2`
  font-size: 1.6rem;
  margin-bottom: 0.8rem;
  color: #fff;
  
  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.2rem;
    margin-bottom: 0.6rem;
  }
`;

const GatewayFeatures = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-bottom: 1rem;
  
  @media (max-width: 480px) {
    gap: 0.4rem;
    margin-bottom: 0.8rem;
  }
`;

const FeatureTag = styled.span`
  background-color: #2a2a2a;
  padding: 0.4rem 0.8rem;
  border-radius: 4px;
  font-size: 0.9rem;
  white-space: nowrap;
  
  @media (max-width: 480px) {
    padding: 0.3rem 0.6rem;
    font-size: 0.8rem;
    border-radius: 3px;
  }
`;

const GatewayURL = styled.p`
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
  color: #6e8efb;
  word-break: break-all;
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
    margin-bottom: 1.2rem;
  }
  
  @media (min-width: 768px) {
    margin-bottom: 0;
  }
`;

const ButtonWrapper = styled.div`
  @media (min-width: 768px) {
    margin-left: 2rem;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    display: flex;
    justify-content: center;
  }
`;

// Array com as opções de gateway dinâmicas com base nas respostas do usuário
const getGatewayOptions = (answers: any) => {
  const { billingVolume, region } = answers;

  let gatewayOptions = [
    {
      id: 1,
      name: 'MundPay',
      pricing: '5,99% + R$ 1,50',
      coverage: 'Global',
      payout: 'D+0',
      url: 'https://mundpay.melhortaxaglobal.com'
    },
    {
      id: 2,
      name: 'Bynet',
      pricing: '3,99%',
      coverage: 'Global',
      payout: 'D+0',
      url: 'https://bynet.melhortaxaglobal.com'
    },
    {
      id: 3,
      name: 'BlackPay',
      pricing: '3,99% + R$ 0,99',
      coverage: 'Global',
      payout: 'D+0',
      url: 'https://blackpay.melhortaxaglobal.com'
    }
  ];

  // Se o volume de faturamento for acima de 100k, dê um desconto especial
  if (billingVolume === '100k a 1M' || billingVolume === '1M a 10M' || billingVolume === '+10M') {
    gatewayOptions = gatewayOptions.map(gateway => ({
      ...gateway,
      pricing: gateway.pricing.includes('%')
        ? gateway.pricing.replace(/(\d+,\d+)%/, (match) => {
          const rate = parseFloat(match.replace(',', '.'));
          const newRate = (rate - 0.5).toFixed(2).replace('.', ',');
          return `${newRate}%`;
        })
        : gateway.pricing
    }));
  }

  // Para o Brasil, adicione PayZen
  if (region === 'Brasil') {
    gatewayOptions.push({
      id: 4,
      name: 'PayZen',
      pricing: '4,99%',
      coverage: 'Brasil',
      payout: 'D+1',
      url: 'https://payzen.melhortaxaglobal.com'
    });
  }

  return gatewayOptions;
};

const Results: React.FC = () => {
  const { answers, resetQuiz } = useQuiz();
  const navigate = useNavigate();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState<any>(null);
  const { user } = useUser();

  // Verificar se há respostas válidas quando o componente monta
  useEffect(() => {
    // Tentar reenviar webhooks anteriores que podem ter falhado
    retryPendingWebhooks();

    // Verificar se o Facebook Pixel está carregado
    if (typeof window.fbq !== 'function') {
      // Tentar reinicializar o Facebook Pixel
      try {
        // Recarregar o script do Facebook Pixel manualmente
        const script = document.createElement('script');
        script.innerHTML = `
          !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '1206177091200862');
          // PageView já é enviado pelo PageTracker no App.tsx
        `;
        document.head.appendChild(script);
      } catch (e) {
        // Silently fail
      }
    }

    // Verificar se há respostas válidas
    const hasValidResponses = answers &&
      answers.billingVolume &&
      answers.region &&
      answers.platform &&
      answers.email &&
      answers.phone;

    // Se não houver respostas válidas, redireciona para o início
    if (!hasValidResponses) {
      resetQuiz();
      navigate('/');
      return;
    }

    // Registrar visualização da página de resultados no localStorage
    const resultViewsCount = parseInt(localStorage.getItem('bt_result_views') || '0', 10);
    localStorage.setItem('bt_result_views', (resultViewsCount + 1).toString());
    localStorage.setItem('bt_last_result_view', Date.now().toString());
  }, [answers, navigate, resetQuiz]);

  // Obter gateways com base nas respostas
  const gatewayOptions = getGatewayOptions(answers);

  // Função para lidar com o clique no gateway
  const handleGatewayClick = async (gateway: any) => {
    // Evitar cliques duplicados
    if (isRedirecting) return;
    setIsRedirecting(true);
    setSelectedGateway(gateway);

    // Verificar duplicatas usando sessionStorage
    const recentEvents = JSON.parse(sessionStorage.getItem('bt_recent_clicks') || '[]');
    const now = Date.now();
    const recentEventsFiltered = recentEvents.filter((event: any) =>
      now - event.timestamp < 3000 && // 3 segundos
      event.gatewayName === gateway.name
    );

    if (recentEventsFiltered.length > 0) {
      console.warn('Clique duplicado detectado');
      return;
    }

    // Registrar o clique atual
    recentEvents.push({
      gatewayName: gateway.name,
      timestamp: now
    });
    sessionStorage.setItem('bt_recent_clicks', JSON.stringify(recentEvents));

    // Preparar dados para tracking
    const trackingData = {
      user: user ? {
        id: user.id,
        email: user.email,
        name: user.name
      } : {
        id: 'anonymous',
        email: answers.email,
        name: 'Usuário Anônimo'
      },
      quiz_data: {
        billing_volume: answers.billingVolume,
        region: answers.region,
        platform: answers.platform,
        integration: answers.integration
      },
      search: {
        resultsCount: gatewayOptions.length
      },
      gateway: {
        name: gateway.name,
        url: gateway.url,
        position: gatewayOptions.findIndex(g => g.id === gateway.id) + 1
      }
    };

    try {
      // Enviar tracking
      await trackGatewaySelection(gateway.name, gateway.url, trackingData);
    } catch (error) {
      console.warn('Erro ao enviar tracking:', error);
    }

    // Redirecionar após um pequeno delay para garantir que o tracking seja tentado
    setTimeout(() => {
      window.location.href = gateway.url;
    }, 1000);
  };

  return (
    <PageContainer>
      <ResultsHeader>
        {isRedirecting ? (
          <>
            <Title>
              Redirecionando para {selectedGateway?.name}...
            </Title>
            <Subtitle>
              Estamos te conectando com o melhor gateway para o seu negócio. Aguarde...
            </Subtitle>
          </>
        ) : (
          <>
            <Title>
              Seus Gateways Recomendados
            </Title>
            <Subtitle>
              Com base nas suas respostas, selecionamos os gateways de pagamento que melhor se adaptam às suas necessidades:
            </Subtitle>
          </>
        )}
      </ResultsHeader>

      {!isRedirecting && (
        <ResultsWrapper>
          {gatewayOptions.map((gateway, index) => (
            <AnimatedCard
              key={gateway.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
            >
              <GatewayInfo>
                <GatewayName>
                  {index + 1}. {gateway.name}
                </GatewayName>
                <GatewayFeatures>
                  <FeatureTag>{gateway.pricing}</FeatureTag>
                  <FeatureTag>{gateway.coverage}</FeatureTag>
                  <FeatureTag>{gateway.payout}</FeatureTag>
                </GatewayFeatures>
                <GatewayURL>{gateway.url}</GatewayURL>
              </GatewayInfo>
              <ButtonWrapper>
                <LinkButton
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleGatewayClick(gateway);
                  }}
                >
                  Conhecer Gateway
                </LinkButton>
              </ButtonWrapper>
            </AnimatedCard>
          ))}
        </ResultsWrapper>
      )}
    </PageContainer>
  );
};

export default Results; 